import React, { useState, useEffect, useCallback } from 'react';
import { ArrowDown, SearchIcon } from '../../../public/icons';
import Pagination from '../../shared/Pagination';
import { useToast } from '../../../context/ToastContext';
import adminAddonService from '../../../services/admin/admin-addonService'; // ✅ FIXED: Use correct service

const AddOnsTab = ({ refreshTrigger }) => { // ✅ ADD: refreshTrigger prop
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    totalItems: 0
  });

  const { success, error } = useToast();

  const statusOptions = ['All Status', 'active', 'draft', 'inactive', 'archived'];

  // ============ FETCH ADDONS - WITH useCallback ============
  const fetchAddons = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      console.log('🔄 [AddOnsTab] Fetching add-ons...', { page, searchQuery, statusFilter });

      const params = {
        page,
        limit: pagination.itemsPerPage,
        search: searchQuery,
        status: statusFilter !== 'All Status' ? statusFilter : undefined,
        sort: '-createdAt'
      };

      // ✅ FIXED: Use adminAddonService, NOT adminProductService
      const response = await adminAddonService.getAddons(params);
      
      if (response.success) {
        console.log('✅ [AddOnsTab] Add-ons fetched:', response.data.addons.length);
        setAddOns(response.data.addons);
        setPagination(prev => ({
          ...prev,
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.pages,
          totalItems: response.data.pagination.total
        }));
      } else {
        error(response.message || 'Failed to fetch add-ons');
        setAddOns([]);
      }
    } catch (err) {
      console.error('💥 [AddOnsTab] Error fetching add-ons:', err);
      error('Unable to load add-ons. Please check your connection.');
      setAddOns([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, pagination.itemsPerPage, error]);

  // ============ FETCH ON MOUNT AND WHEN REFRESH TRIGGER CHANGES ============
  useEffect(() => {
    fetchAddons(pagination.currentPage);
  }, [fetchAddons, pagination.currentPage, refreshTrigger]); // ✅ ADD: refreshTrigger dependency

  // ============ DEBOUNCED SEARCH ============
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.currentPage === 1) {
        fetchAddons(1);
      } else {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, fetchAddons]);

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    fetchAddons(pageNumber);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        textColor: 'text-[#2F8F5B]',
        bgColor: 'bg-[#E7F4ED]',
        dotColor: 'bg-[#2F8F5B]',
        label: 'Active'
      },
      draft: {
        textColor: 'text-[#B8860B]',
        bgColor: 'bg-[#FFF9E6]',
        dotColor: 'bg-[#B8860B]',
        label: 'Draft'
      },
      inactive: {
        textColor: 'text-[#6B7280]',
        bgColor: 'bg-[#F3F4F6]',
        dotColor: 'bg-[#6B7280]',
        label: 'Inactive'
      },
      archived: {
        textColor: 'text-[#8F342F]',
        bgColor: 'bg-[#F4E7E7]',
        dotColor: 'bg-[#8F342F]',
        label: 'Archived'
      }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`flex items-center gap-1.5 px-3 py-1.5 text-xs leading-5 font-semibold rounded-full ${config.bgColor}`}>
        <span className={`h-2 w-2 rounded-full ${config.dotColor}`} />
        <span className={config.textColor}>
          {config.label}
        </span>
        <ArrowDown size={12} className={config.textColor} />
      </span>
    );
  };

  const handleActionClick = async (addOnId, action) => {
    try {
      switch (action) {
        case 'edit':
          console.log('Edit add-on:', addOnId);
          // TODO: Open edit modal
          break;
        
        case 'toggle-status':
          const addon = addOns.find(a => a.id === addOnId);
          const newStatus = addon.status === 'active' ? 'inactive' : 'active';
          
          console.log(`🔄 [AddOnsTab] Toggling status for ${addon.name} to ${newStatus}`);
          
          // ✅ FIXED: Use adminAddonService
          const response = await adminAddonService.updateAddonStatus(addOnId, newStatus);
          
          if (response.success) {
            success(`Add-on status updated to ${newStatus}`);
            fetchAddons(pagination.currentPage);
          } else {
            error(response.message || 'Failed to update status');
          }
          break;
        
        case 'delete':
          if (window.confirm('Are you sure you want to delete this add-on?')) {
            console.log(`🔄 [AddOnsTab] Deleting add-on:`, addOnId);
            
            // ✅ FIXED: Use adminAddonService
            const deleteResponse = await adminAddonService.deleteAddon(addOnId);
            
            if (deleteResponse.success) {
              success('Add-on deleted successfully');
              fetchAddons(pagination.currentPage);
            } else {
              error(deleteResponse.message || 'Failed to delete add-on');
            }
          }
          break;
        
        default:
          console.log('Action menu opened for:', addOnId);
      }
    } catch (err) {
      console.error('💥 [AddOnsTab] Add-on action error:', err);
      error('Failed to perform action. Please try again.');
    }
  };

  if (loading && addOns.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55000C]"></div>
        <span className="ml-3 text-sm text-gray-600">Loading add-ons...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add-ons Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-medium text-[#0E1628]">Add-ons</h2>
          
          <div className="flex gap-4 items-center">
            {/* Search Box */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={20} color="#6B635D" />
              </div>
              <input
                type="text"
                placeholder="Search add-ons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="p-2 pl-10 pr-3 border w-52 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="p-2 pl-3 pr-8 border w-40 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ArrowDown size={16} color="#6B635D" />
              </div>
            </div>
          </div>
        </div>

        {/* Add-ons Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left">
            <thead className="bg-[#F3F4F6]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Add-On Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Base Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Variants
                </th>
                <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {addOns.map((addOn) => (
                <tr key={addOn.id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <span className="text-sm font-medium text-[#0E1628]">{addOn.name}</span>
                      {addOn.shortDescription && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                          {addOn.shortDescription}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-[#0E1628]">
                      {addOn.formattedPrice || `₦${addOn.basePrice?.toLocaleString() || '0'}`}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                      {addOn.isFunctional ? (addOn.contentType || 'Functional') : 'Standard'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {addOn.variantCount || 0} variants
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(addOn.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="relative">
                      <button
                        onClick={() => handleActionClick(addOn.id, 'menu')}
                        className="text-[#6B635D] hover:text-[#1C2A46] transition-colors px-2 py-1"
                      >
                        <span className="text-lg font-bold">⋮</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Empty State */}
        {addOns.length === 0 && !loading && (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-400 mb-2">No add-ons found</div>
            <div className="text-sm text-gray-500">
              {searchQuery || statusFilter !== 'All Status' 
                ? 'Try adjusting your search or filter'
                : 'Add your first add-on to get started'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddOnsTab;